// TemplateThumbnail.tsx — tarjeta de preview de un template global.
// Qué hace: carga el template por id, renderiza un thumbnail compuesto con un
//   background de muestra y textos dinámicos de ejemplo.
// Qué exporta: TemplateThumbnail.
// Quién lo usa: el step de template-select (TemplateSelectStep).

import { useState, useEffect } from 'react';
import { Template } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { renderCompositeThumbnail } from '../../utils/canvasRenderer';
import { TplCard, TplPreview, TplName, Badge, PREVIEW_SAMPLE_IMAGES, SAMPLE_DYNAMIC_TEXTS } from './styled';

interface Props {
  templateId: string;
  selected: boolean;
  onClick: () => void;
}

export function TemplateThumbnail({ templateId, selected, onClick }: Props) {
  const [name, setName] = useState('');
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tpl = await apiClient.get<Template>(`/api/workspaces/__global__/templates/${templateId}`);
        if (cancelled) return;
        setName(tpl.name);
        const hash = templateId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
        const bg = PREVIEW_SAMPLE_IMAGES[hash % PREVIEW_SAMPLE_IMAGES.length];

        // Build dynamic texts map from template elements
        const dynamicTexts: Record<string, string> = {};
        for (const el of tpl.elements) {
          if (el.elementType === 'text' && (el as { isDynamic?: boolean }).isDynamic) {
            const dt = (el as { dynamicType?: string }).dynamicType || 'headline';
            if (SAMPLE_DYNAMIC_TEXTS[dt]) dynamicTexts[el.id] = SAMPLE_DYNAMIC_TEXTS[dt];
          }
        }

        const url = await renderCompositeThumbnail(bg, tpl, 200, { dynamicTexts });
        if (cancelled) return;
        setThumbUrl(url);
        setLoaded(true);
      } catch {
        setName('Template');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  return (
    <TplCard $selected={selected} onClick={onClick}>
      <TplPreview>
        {thumbUrl ? (
          <img src={thumbUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 10, color: '#999' }}>{loaded ? name : 'Loading...'}</span>
        )}
      </TplPreview>
      <TplName>{name}</TplName>
      {selected && <Badge>✓</Badge>}
    </TplCard>
  );
}
